import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {Button, Card, CardBody, CardHeader, Table} from "reactstrap";
import Skeleton from "react-loading-skeleton";
import Tag from "../../atoms/tag/tag";
import {useStores} from "../../../stores/useStores";
import {RoutePaths} from "../../../shared/routePaths";
import {VillageCalendarConfig} from "../../../shared/interfaces/villageCalendarConfig";
import {multiVillageKeyValue} from "../../../shared/constants";
import {ToastMessageStatus} from "../../atoms/toastMessage/toastMessage";
import "./villageCalendarsTemplate.sass";

/**
 * List page for the calendars of the village selected in the nav village dropdown.
 */
const VillageCalendarsTemplate: React.FC = () => {
  const {t} = useTranslation(["translation", "villageCalendars"]);
  const history = useHistory();
  const {
    domainStore: {villageCalendarsDomainStore, personTypesDomainStore},
    uiStore: {
      villageCalendarsUiStore,
      modalStore,
      toastMessageStore,
      selectableVillages
    }
  } = useStores();

  const selectedVillageId = selectableVillages.selectedItem?.value;
  // Multi-Village is a synthetic option with no calendars of its own.
  const isSingleVillageSelected =
    !!selectedVillageId && selectedVillageId !== multiVillageKeyValue.value;
  const selectedVillageCalendars = isSingleVillageSelected
    ? villageCalendarsDomainStore.villageCalendarsByVillage.find(
        (g) => g.villageId === String(selectedVillageId)
      )?.calendars ?? []
    : [];

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        villageCalendarsUiStore.setLoading(true);
        villageCalendarsUiStore.setErrorMessage("");
        await villageCalendarsDomainStore.getVillageCalendars(controller);
      } catch (err) {
        villageCalendarsUiStore.setErrorMessage(
          t("villageCalendars:messages.loadError")
        );
      } finally {
        villageCalendarsUiStore.setLoading(false);
      }
    })();
    if (!personTypesDomainStore.isLoaded) {
      personTypesDomainStore.loadPersonTypes().catch(() => {
        /* names fall back to raw ids until the fetch succeeds */
      });
    }
    return () => controller.abort();
  }, [
    villageCalendarsDomainStore,
    villageCalendarsUiStore,
    personTypesDomainStore,
    t
  ]);

  const goToCreate = (villageId: string) =>
    history.push(
      `${RoutePaths.VillageCalendarsCreate}?forVillageId=${encodeURIComponent(
        villageId
      )}`
    );

  const goToEdit = (id: string) =>
    history.push(RoutePaths.VillageCalendarsEdit.replace(":id", id));

  const deleteCalendar = async (calendar: VillageCalendarConfig) => {
    if (!calendar.id) return;
    try {
      villageCalendarsUiStore.setDeleting(true);
      await villageCalendarsDomainStore.deleteVillageCalendar(calendar.id);
      toastMessageStore.displayToastMessage(
        t("villageCalendars:messages.deleteSuccess", {name: calendar.name}),
        ToastMessageStatus.Success
      );
    } catch (err) {
      toastMessageStore.displayToastMessage(
        t("villageCalendars:messages.deleteError"),
        ToastMessageStatus.Danger
      );
    } finally {
      villageCalendarsUiStore.setDeleting(false);
    }
  };

  const confirmDelete = (calendar: VillageCalendarConfig) => {
    modalStore.openModalWithContent({
      content: t("villageCalendars:messages.deleteConfirm", {
        name: calendar.name
      }),
      title: t("villageCalendars:titles.delete"),
      primaryButtonText: t("translation:confirmations.affirmativeDelete"),
      secondaryButtonText: t("translation:confirmations.negative"),
      centered: true,
      dangerButton: true,
      onPrimaryClick: () => {
        deleteCalendar(calendar);
        modalStore.toggle();
      },
      onSecondaryClick: modalStore.toggle
    });
  };

  const renderResidentGroups = (c: VillageCalendarConfig) => {
    const {text, isLegacy} = villageCalendarsUiStore.getResidentGroupsDisplay(
      c.personTypeIds,
      c.personType
    );
    return (
      <>
        {text}
        {isLegacy && (
          <Tag type="neutral" className="ml-2">
            {t("villageCalendars:tags.legacyPersonType")}
          </Tag>
        )}
      </>
    );
  };

  const renderCalendarsTable = (calendars: VillageCalendarConfig[]) => (
    <Table responsive size="sm" className="village-calendars-template__table">
      <thead>
        <tr>
          <th>{t("villageCalendars:labels.name")}</th>
          <th>{t("villageCalendars:labels.calendarType")}</th>
          <th>{t("villageCalendars:labels.calendarColor")}</th>
          <th>{t("villageCalendars:labels.personType")}</th>
          <th>{t("villageCalendars:labels.hasCareCompanion")}</th>
          <th className="text-end">{t("villageCalendars:labels.actions")}</th>
        </tr>
      </thead>
      <tbody>
        {calendars.map((c) => (
          <tr key={c.id ?? `${c.villageId}-${c.name}`}>
            <td>{c.name}</td>
            <td>{c.calendarType}</td>
            <td>
              <span
                className="village-calendars-template__swatch"
                style={{backgroundColor: c.calendarColor}}
                aria-label={c.calendarColor}
              />
              {c.calendarColor}
            </td>
            <td>{renderResidentGroups(c)}</td>
            <td>
              {c.hasCareCompanion
                ? t("villageCalendars:common.yes")
                : t("villageCalendars:common.no")}
            </td>
            <td className="text-end">
              <Button
                size="sm"
                color="link"
                onClick={() => c.id && goToEdit(c.id)}
                disabled={!c.id}
                data-testid={`edit-calendar-${c.id}`}
              >
                {t("villageCalendars:buttons.edit")}
              </Button>
              <Button
                size="sm"
                color="link"
                className="text-danger"
                onClick={() => confirmDelete(c)}
                disabled={!c.id || villageCalendarsUiStore.isDeleting}
                data-testid={`delete-calendar-${c.id}`}
              >
                {t("villageCalendars:buttons.delete")}
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <div
      className="village-calendars-template"
      data-testid="villageCalendarsTemplate"
    >
      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between">
          <h4 className="mb-0">{t("villageCalendars:title")}</h4>
          <Button
            color="primary"
            onClick={() => goToCreate(String(selectedVillageId))}
            disabled={!isSingleVillageSelected}
            data-testid="create-village-calendar"
          >
            {t("villageCalendars:buttons.create")}
          </Button>
        </CardHeader>
        <CardBody>
          {villageCalendarsUiStore.isLoading && (
            <Skeleton height={40} count={4} />
          )}
          {!!villageCalendarsUiStore.errorMessage && (
            <div className="text-danger" role="alert">
              {villageCalendarsUiStore.errorMessage}
            </div>
          )}
          {!villageCalendarsUiStore.isLoading &&
            !villageCalendarsUiStore.errorMessage &&
            (!isSingleVillageSelected ? (
              <div className="text-muted">
                {t("villageCalendars:messages.selectVillage")}
              </div>
            ) : selectedVillageCalendars.length === 0 ? (
              <div className="text-muted">
                {t("villageCalendars:messages.noCalendars")}
              </div>
            ) : (
              renderCalendarsTable(selectedVillageCalendars)
            ))}
        </CardBody>
      </Card>
    </div>
  );
};

export default observer(VillageCalendarsTemplate);
